// The course a user picks at sign-up (`userbackground` on the user record).
// Mirrors the options on the Auth0 sign-up form — keep the two in sync.
export const USER_BACKGROUNDS = [
  'A-Level Student',
  'GCSE Student',
  'School Teacher',
  'Private Tutor',
  'Other',
] as const;

export type UserBackground = (typeof USER_BACKGROUNDS)[number];

// Order matters: the search page's history router writes refinements to the URL in
// widget mount order, and the search page mounts one headless widget per attribute
// in this order, so suggestion URLs built from it match what the router writes.
// New attributes go last so existing suggestion URLs keep their order.
export const SUGGESTION_ATTRIBUTES = [
  'doc_exam_course',
  'doc_exam_board',
  'doc_type',
  'doc_exam_modules',
] as const;

export type SuggestionAttribute = (typeof SUGGESTION_ATTRIBUTES)[number];

export interface SearchSuggestion {
  id: string;
  label: string;
  // Values must match the Typesense facet values exactly, e.g. 'A Level (GCE)'.
  refinements: Partial<Record<SuggestionAttribute, string[]>>;
}

// Each suggestion is defined once, then listed per course (and for logged-out visitors) below.
// Ids are the `search_suggestion_clicked` analytics label, so keep them when changing a label.
const A_LEVEL_PAST_PAPERS: SearchSuggestion = {
  id: 'a-level-past-papers',
  label: 'A Level Past Papers',
  refinements: { doc_exam_course: ['A Level (GCE)'], doc_type: ['Past Papers'] },
};

const A_LEVEL_EDEXCEL_PAST_PAPERS: SearchSuggestion = {
  id: 'a-level-edexcel-past-papers',
  label: 'Edexcel A Level Past Papers',
  refinements: {
    doc_exam_course: ['A Level (GCE)'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Past Papers'],
  },
};

const AS_LEVEL_PAST_PAPERS: SearchSuggestion = {
  id: 'as-level-past-papers',
  label: 'AS Level Past Papers',
  refinements: { doc_exam_course: ['AS Level (GCE)'], doc_type: ['Past Papers'] },
};

const AS_LEVEL_EDEXCEL_PAST_PAPERS: SearchSuggestion = {
  id: 'as-level-edexcel-past-papers',
  label: 'Edexcel AS Past Papers',
  refinements: {
    doc_exam_course: ['AS Level (GCE)'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Past Papers'],
  },
};

const A_LEVEL_FURTHER_EDEXCEL_PAST_PAPERS: SearchSuggestion = {
  id: 'a-level-further-edexcel-past-papers',
  label: 'Edexcel A Level Further Past Papers',
  refinements: {
    doc_exam_course: ['A Level Further (GCE)'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Past Papers'],
  },
};

const A_LEVEL_EDEXCEL_TOPIC_TESTS: SearchSuggestion = {
  id: 'a-level-edexcel-topic-tests',
  label: 'Edexcel A Level Topic Tests',
  refinements: {
    doc_exam_course: ['A Level (GCE)'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Topic Tests'],
  },
};

const A_LEVEL_EDEXCEL_AIMING_FOR_A_STAR: SearchSuggestion = {
  id: 'a-level-edexcel-aiming-for-a-star',
  label: 'Edexcel A Level Aiming for A*',
  refinements: {
    doc_exam_course: ['A Level (GCE)'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Aiming For A*'],
  },
};

const A_LEVEL_EDEXCEL_PRACTICE_PAPERS: SearchSuggestion = {
  id: 'a-level-edexcel-practice-papers',
  label: 'Edexcel A Level Practice Papers',
  refinements: {
    doc_exam_course: ['A Level (GCE)'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Practice Papers'],
  },
};

const A_LEVEL_CHEAT_SHEETS: SearchSuggestion = {
  id: 'a-level-cheat-sheets',
  label: 'A Level Cheat Sheets',
  refinements: { doc_exam_course: ['A Level (GCE)'], doc_type: ['Cheat Sheets'] },
};

const IAL_EDEXCEL_PAST_PAPERS: SearchSuggestion = {
  id: 'ial-edexcel-past-papers',
  label: 'Edexcel IAL Past Papers',
  refinements: {
    doc_exam_course: ['International A Level'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Past Papers'],
  },
};

// P1 is an Edexcel International A Level module.
const IAL_EDEXCEL_P1_PAST_PAPERS: SearchSuggestion = {
  id: 'ial-edexcel-p1-past-papers',
  label: 'Edexcel P1 Past Papers',
  refinements: {
    doc_exam_course: ['International A Level'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Past Papers'],
    doc_exam_modules: ['P1'],
  },
};

const GCSE_PAST_PAPERS: SearchSuggestion = {
  id: 'gcse-past-papers',
  label: 'GCSE Past Papers',
  refinements: { doc_exam_course: ['GCSE'], doc_type: ['Past Papers'] },
};

const GCSE_EDEXCEL_PAST_PAPERS: SearchSuggestion = {
  id: 'gcse-edexcel-past-papers',
  label: 'Edexcel GCSE Past Papers',
  refinements: {
    doc_exam_course: ['GCSE'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Past Papers'],
  },
};

const GCSE_EDEXCEL_PRACTICE_PAPERS: SearchSuggestion = {
  id: 'gcse-edexcel-practice-papers',
  label: 'Edexcel GCSE Practice Papers',
  refinements: {
    doc_exam_course: ['GCSE'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Practice Papers'],
  },
};

const GCSE_EDEXCEL_TOPIC_TESTS: SearchSuggestion = {
  id: 'gcse-edexcel-topic-tests',
  label: 'Edexcel GCSE Topic Tests',
  refinements: {
    doc_exam_course: ['GCSE'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Topic Tests'],
  },
};

const GCSE_EDEXCEL_AIMING_FOR_9: SearchSuggestion = {
  id: 'gcse-edexcel-aiming-for-9',
  label: 'Edexcel GCSE Aiming for 9',
  refinements: {
    doc_exam_course: ['GCSE'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Aiming For Grade 9 Worksheets (Guaranteed Grade 9)'],
  },
};

const IGCSE_EDEXCEL_PAST_PAPERS: SearchSuggestion = {
  id: 'igcse-edexcel-past-papers',
  label: 'Edexcel IGCSE Past Papers',
  refinements: {
    doc_exam_course: ['IGCSE'],
    doc_exam_board: ['Edexcel'],
    doc_type: ['Past Papers'],
  },
};

const GCSE_AQA_PAST_PAPERS: SearchSuggestion = {
  id: 'gcse-aqa-past-papers',
  label: 'AQA GCSE Past Papers',
  refinements: {
    doc_exam_course: ['GCSE'],
    doc_exam_board: ['AQA'],
    doc_type: ['Past Papers'],
  },
};

const GCSE_FURTHER_AQA_PAST_PAPERS: SearchSuggestion = {
  id: 'gcse-further-aqa-past-papers',
  label: 'AQA GCSE Further Past Papers',
  refinements: {
    doc_exam_course: ['GCSE Further'],
    doc_exam_board: ['AQA'],
    doc_type: ['Past Papers'],
  },
};

const A_LEVEL_POPULAR = [
  A_LEVEL_EDEXCEL_PAST_PAPERS,
  AS_LEVEL_EDEXCEL_PAST_PAPERS,
  A_LEVEL_FURTHER_EDEXCEL_PAST_PAPERS,
  A_LEVEL_EDEXCEL_TOPIC_TESTS,
  A_LEVEL_EDEXCEL_AIMING_FOR_A_STAR,
  A_LEVEL_EDEXCEL_PRACTICE_PAPERS,
  A_LEVEL_CHEAT_SHEETS,
  IAL_EDEXCEL_PAST_PAPERS,
  IAL_EDEXCEL_P1_PAST_PAPERS,
];

const GCSE_POPULAR = [
  GCSE_EDEXCEL_PAST_PAPERS,
  GCSE_EDEXCEL_PRACTICE_PAPERS,
  GCSE_EDEXCEL_TOPIC_TESTS,
  GCSE_EDEXCEL_AIMING_FOR_9,
  IGCSE_EDEXCEL_PAST_PAPERS,
  GCSE_FURTHER_AQA_PAST_PAPERS,
];

// Shown to logged-out visitors as "Popular searches".
export const DEFAULT_SEARCH_SUGGESTIONS: SearchSuggestion[] = [...A_LEVEL_POPULAR, ...GCSE_POPULAR];

// Shown to logged-in users by the course in their profile. Courses without an entry get none.
export const SEARCH_SUGGESTIONS: Partial<Record<UserBackground, SearchSuggestion[]>> = {
  'A-Level Student': [...A_LEVEL_POPULAR, A_LEVEL_PAST_PAPERS, AS_LEVEL_PAST_PAPERS],
  'GCSE Student': [...GCSE_POPULAR, GCSE_PAST_PAPERS, GCSE_AQA_PAST_PAPERS],
};
